import { isDemoMode } from "@/lib/runtimeMode";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  enablePushNotifications,
  defaultPushPreferences,
  detachPush,
  pushRequest,
  readPushState,
  type PushPreferences,
} from "./pushClient";
export function PushPreferencesPanel() {
  const supported =
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    window.isSecureContext;
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [preferences, setPreferences] = useState(defaultPushPreferences);
  const [checking, setChecking] = useState(false);
  const generation = useRef(0);
  const working = useRef(false);
  const cancelCheck = useCallback(() => {
    ++generation.current;
  }, []);
  const check = useCallback(async () => {
    if (working.current) return;
    const request = ++generation.current;
    setChecking(true);
    try {
      const result = await readPushState();
      if (request !== generation.current) return;
      setSubscribed(result.subscribed);
      setPreferences(result.preferences);
      setMessage("");
    } catch {
      if (request !== generation.current) return;
      setSubscribed(null);
      setMessage(
        "Не удалось проверить уведомления. Сохранённая подписка не отключена. Повторите проверку при восстановлении связи.",
      );
    } finally {
      if (request === generation.current) setChecking(false);
    }
  }, []);
  useEffect(() => {
    if (!supported || isDemoMode()) {
      setSubscribed(false);
      return;
    }
    void check();
    const resume = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      cancelCheck();
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [supported, check, cancelCheck]);
  const enable = async () => {
    ++generation.current;
    working.current = true;
    setChecking(false);
    setBusy(true);
    setMessage("");
    try {
      await enablePushNotifications(preferences);
      setSubscribed(true);
      setMessage("Уведомления включены на этом устройстве.");
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      working.current = false;
      setBusy(false);
    }
  };
  const change = async (key: keyof PushPreferences, value: boolean) => {
    const next = { ...preferences, [key]: value };
    ++generation.current;
    working.current = true;
    setChecking(false);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (!sub)
        throw new Error("Подписка не найдена. Включите уведомления повторно.");
      await pushRequest({
        action: "preferences",
        endpoint: sub.endpoint,
        preferences: next,
      });
      setPreferences(next);
      setMessage("Настройки уведомлений сохранены.");
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      working.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="rounded-2xl border p-4 space-y-4">
      <h2 className="text-lg font-semibold">Push-уведомления</h2>
      <p className="text-sm text-muted-foreground">
        Получайте оповещения о своих задачах на этом телефоне или компьютере,
        даже когда CRM закрыта. На экране блокировки показывается только тип
        события.
      </p>
      {!supported && (
        <p className="text-sm">
          В этом браузере push недоступен. На iPhone добавьте CRM на экран
          «Домой» и откройте её через значок приложения.
        </p>
      )}
      {supported && Notification.permission === "denied" && (
        <p className="text-sm">
          Уведомления заблокированы браузером. Разрешите их в настройках сайта.
        </p>
      )}
      <Button
        type="button"
        disabled={!supported || busy || checking}
        onClick={
          subscribed === null
            ? () => void check()
            : subscribed
              ? async () => {
                  ++generation.current;
                  working.current = true;
                  setChecking(false);
                  setBusy(true);
                  try {
                    await detachPush();
                    setSubscribed(false);
                    setMessage("Уведомления выключены на этом устройстве.");
                  } catch (error) {
                    setMessage((error as Error).message);
                  } finally {
                    working.current = false;
                    setBusy(false);
                  }
                }
              : enable
        }
      >
        {checking
          ? "Проверяем уведомления…"
          : busy
            ? "Сохраняем…"
            : subscribed === null
              ? "Повторить проверку"
              : subscribed
                ? "Выключить на этом устройстве"
                : "Включить уведомления"}
      </Button>
      {subscribed && (
        <div className="space-y-3">
          {(
            [
              ["assigned", "Мне назначили задачу"],
              ["status", "Изменился статус моей или порученной мной задачи"],
              ["comments", "Новый комментарий в моей задаче"],
              ["due", "За час до срока выполнения"],
            ] as const
          ).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center justify-between gap-4 text-sm"
            >
              <span>{label}</span>
              <Switch
                checked={preferences[key]}
                disabled={busy}
                onCheckedChange={(value) => change(key, value)}
              />
            </label>
          ))}
        </div>
      )}
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
    </section>
  );
}
